import { ServerEvent } from "@/common/notification";
import {
	MessagingInviteAction,
	MessagingInviteState,
	useMessagingInviteSessionStore,
} from "@/common/stores/nonpersistingStores/messagingInvite";
import { MessagingOfferNotification } from "@/common/models/messagingNotification";
import { useAuth } from "@/hooks/authentication";
import { notificationListener } from "@/hooks/notification";
import { fetchCurrentMessagingInvite } from "@/repository/queryFns";
import { useEffect } from "react";
import { InteractionDto } from "@/repository/models";
import {
	NavigationActions,
	TabType,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import {
	AppCacheActions,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { NavigationState } from "@/common/stores/persistingStores/navigation/types";
import { interactionsQueryKey } from "@/repository/queryFns/interactions";
import { useQueryClient } from "@tanstack/react-query";
import { useMessage } from "@/common/messages";
import { useIntl } from "react-intl";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import {
	useWebchatCustomerClosedStore,
	WebchatCustomerClosedActions,
} from "@/common/stores/nonpersistingStores/webchatCustomerClosed";

export const useMessagingInviteSessionManager = () => {
	const queryKey = [interactionsQueryKey];
	const authContext = useAuth();
	const queryClient = useQueryClient();
	const updateMessagingInviteSession = useMessagingInviteSessionStore(
		(actions: MessagingInviteAction) => actions.update
	);
	const { show: showMessage } = useMessage();
	const intl = useIntl();
	const markCustomerClosed = useWebchatCustomerClosedStore(
		(actions: WebchatCustomerClosedActions) => actions.markClosed
	);

	const activeInteractionsCount = useNavigationStore(
		(state: NavigationState) => state.activeInteractionsCount
	);

	const messagingInviteSession = useMessagingInviteSessionStore(
		(state: MessagingInviteState) => state.messagingSession
	);

	const selectedTab = useNavigationStore(
		(state: NavigationState) => state.selectedTab
	);

	const setSelectedTab = useNavigationStore(
		(state: NavigationActions) => state.setSelectedTab
	);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	useEffect(() => {
		if (!authContext.isLogged) {
			return;
		}

		fetchCurrentMessagingInvite().then(updateMessagingInviteSession);
	}, [authContext.isLogged]);

	notificationListener(
		ServerEvent.MessagingOffered,
		(messagingInvite: MessagingOfferNotification) => {
			updateMessagingInviteSession(messagingInvite);
		}
	);

	notificationListener(
		ServerEvent.MessagingSessionRemoved,
		(sessionId: string) => {
			if (messagingInviteSession?.id == sessionId) {
				updateMessagingInviteSession(null);
			}
		}
	);

	notificationListener(
		ServerEvent.WebchatCustomerClosed,
		(sessionId: string) => {
			// Remember it, not just toast it: the agent keeps the interaction until they close it, and
			// actions that need the customer on the other end (starting a video call) must be blocked.
			markCustomerClosed(sessionId);

			const namespace =
				currentInteraction?.id == sessionId ? "workspace" : "global";

			showMessage(namespace, {
				intent: "warning",
				text: intl.formatMessage({
					id: "Interaction.Messages.WebchatCustomerClosed",
				}),
			});
		}
	);

	notificationListener(
		ServerEvent.MessagingInteractionClosedByInactivity,
		(_conversationId: string) => {
			// Always route to "global": this is a terminal close, so the interaction's own
			// "workspace" outlet is exactly what's being torn down (current-interaction switch or
			// auto-switch-to-previous-tab), and MessageOutlet's unmount cleanup would wipe the toast
			// before it's seen. The global outlet is app-level and always mounted.
			// 500ms delay is for making sure the notification is not displayed before
			// the interaction is actually removed from the list or at least wrap-up started
			setTimeout(() => {
				showMessage("global", {
					intent: "warning",
					text: intl.formatMessage({
						id: "Interaction.Messages.MessagingInteractionClosedByInactivity",
					}),
				});
			}, 500);
		}
	);

	notificationListener(
		ServerEvent.MessagingAccepted,
		(interaction: InteractionDto) => {
			updateMessagingInviteSession(null);
			appStoreActions.setCache(CacheKey.AcceptedInteractionId, interaction.id);

			if (selectedTab !== TabType.Interactions) {
				setTimeout(() => {
					queryClient.invalidateQueries({ queryKey: queryKey });
				}, 1000);
			}

			if (activeInteractionsCount == 0) {
				appStoreActions.setCache(CacheKey.PreviousTab, selectedTab);
			}

			setSelectedTab(TabType.Interactions);
		}
	);
};
